import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { MethodContent, MethodContentSection } from '@/types/methodologies';

interface LeanCanvasViewProps {
  methodId: string;
  content: MethodContent;
}

/**
 * Classic Lean Canvas positions, extended with the two sub-fields the wave A
 * schema adds (`existing_alternatives` next to Problem, `early_adopters` next
 * to Customer Segments, `high_level_concept` next to UVP). Cost/Revenue sit
 * on their own bottom row, spanning the grid — same as the paper canvas.
 *
 * `mdColSpanClass` only applies from the `md` breakpoint: below it every
 * block is full width and the canvas is a single stack, per spec. Written as
 * literal Tailwind classes (not computed from a number) so the JIT compiler
 * can find them at build time.
 */
const LEAN_CANVAS_BLOCKS: Array<{ key: string; mdColSpanClass?: string }> = [
  { key: 'problem' },
  { key: 'solution' },
  { key: 'uvp' },
  { key: 'unfair_advantage' },
  { key: 'customer_segments' },
  { key: 'existing_alternatives' },
  { key: 'high_level_concept' },
  { key: 'key_metrics' },
  { key: 'channels' },
  { key: 'early_adopters' },
  { key: 'cost_structure', mdColSpanClass: 'md:col-span-2' },
  { key: 'revenue_streams', mdColSpanClass: 'md:col-span-3' },
];

export function LeanCanvasView({ methodId, content }: LeanCanvasViewProps) {
  const { t } = useTranslation('methodologies');
  const byKey = new Map<string, MethodContentSection>(content.sections.map((s) => [s.key, s]));

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-[12px]">
      {LEAN_CANVAS_BLOCKS.map(({ key, mdColSpanClass }) => {
        const section = byKey.get(key);
        if (!section) return null;
        return (
          <div
            key={key}
            className={cn('bg-[#f4f4f3] rounded-[16px] p-[16px] flex flex-col gap-[8px] min-h-[140px]', mdColSpanClass)}
          >
            <h3 className="font-manrope font-semibold text-[13px] leading-[18px] uppercase text-[#565858]">
              {t(`sections.${methodId}.${key}`, { defaultValue: section.title })}
            </h3>
            <ul className="flex flex-col gap-[6px]">
              {section.items.map((item, index) => (
                <li key={index} className="font-segoe text-[13px] leading-[18px] text-[#0d0d0d] pl-[12px] relative">
                  <span className="absolute left-0 top-[7px] w-[3px] h-[3px] rounded-full bg-[#2197f1]" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
